import React, { useEffect, useMemo, useRef, useState } from "react";
import { getOrder, listDeliveryDrivers, listUsers, shipOrder, updateOrderStatus } from "../services/adminDataService";
import { getAddressLabel, getItemName, getLineItems, getOrderTotal, getUserLabel } from "../utils/orderDisplay";

const money = (value) => `₪ ${Number(value || 0).toLocaleString("en-IL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const valueOrDash = (value) => value === undefined || value === null || value === "" ? "—" : value;
const itemTotal = (item) => {
  const explicitTotal = item.lineTotal ?? item.line_total ?? item.total ?? item.totalAmount ?? item.total_amount ?? item.amount;
  if (explicitTotal !== undefined && explicitTotal !== null && explicitTotal !== "") return Number(explicitTotal);
  return Number(item.unitPrice ?? item.unit_price ?? item.price ?? item.pricePerUnit ?? 0) * Number(item.quantity ?? 1);
};
const firstValue = (...values) => values.find((value) => value !== undefined && value !== null && String(value).trim() !== "");
const phoneNumberFor = (order) => {
  const people = [order?.patient, order?.beneficiary, order?.user, order?.customer, order?.client, order?.ownerUser, order?.patientDetails, order?.beneficiaryDetails].filter(Boolean);
  return firstValue(
    order?.phoneNumber, order?.patientPhoneNumber, order?.patientPhone, order?.customerPhone, order?.userPhone, order?.phone, order?.mobile, order?.mobileNumber, order?.contactNumber, order?.phone_number,
    ...people.flatMap((person) => [person.phoneNumber, person.patientPhoneNumber, person.phone, person.mobile, person.mobileNumber, person.contactNumber, person.telephone, person.telephoneNumber, person.phoneNo, person.phone_number]),
  );
};

const Info = ({ label, children }) => <div><span>{label}</span><strong>{valueOrDash(children)}</strong></div>;

const questionnaireFor = (order) => order?.healthQuestionnaire || order?.questionnaire || order?.medicalQuestionnaire || order || {};
const yesNo = (value) => value === true ? "Yes" : value === false ? "No" : "—";
const getAuthenticatedUserId = () => {
  try {
    const user = JSON.parse(localStorage.getItem("authUser") || "{}");
    return user.id ?? user.userId ?? user._id ?? user.adminId ?? null;
  } catch {
    return null;
  }
};

const ReadonlyOption = ({ label, selected, detail }) => <div className={`questionnaire-option ${selected ? "is-selected" : ""}`}><span className="questionnaire-radio" /> <span>{label}</span>{detail && <strong>{detail}</strong>}</div>;

const HealthQuestionnaireModal = ({ order, item, loading, error, onClose }) => {
  const questionnaire = questionnaireFor(order);
  const note = String(questionnaire.pharmacistNote || "").replace(/_/g, " ");
  const noteLabel = note ? note.charAt(0).toUpperCase() + note.slice(1) : "No";
  return <><div className="modal show health-questionnaire-modal" style={{ display: "block" }} tabIndex="-1" role="dialog" aria-modal="true" aria-label={`Health questionnaire for order ${order?.id || item?.id}`} onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}><div className="modal-dialog modal-dialog-centered"><div className="modal-content"><div className="questionnaire-header"><div><small>Order #{order?.id || item?.id}</small><h2>A few quick questions</h2><p>This helps your pharmacist prepare your order safely.</p></div><button type="button" onClick={onClose} aria-label="Close"><i className="bi bi-x-lg" /></button></div><div className="modal-body questionnaire-body">{loading ? <p className="text-muted mb-0">Loading health questionnaire…</p> : <>{error && <div className="alert alert-warning">{error} Showing the available information.</div>}<section className="questionnaire-card"><h3>1. Do you have any medication allergies?</h3><ReadonlyOption label="No" selected={questionnaire.hasAllergies === false} /><ReadonlyOption label="Yes" selected={questionnaire.hasAllergies === true} detail={questionnaire.hasAllergies ? valueOrDash(questionnaire.allergiesDetail) : ""} /></section><section className="questionnaire-card"><h3>2. Are you taking any other medications?</h3><ReadonlyOption label="No" selected={questionnaire.takingOtherMedications === false} /><ReadonlyOption label="Yes" selected={questionnaire.takingOtherMedications === true} detail={questionnaire.takingOtherMedications ? valueOrDash(questionnaire.otherMedicationsDetail) : ""} /></section><section className="questionnaire-card"><h3>3. Anything your pharmacist should know?</h3><ReadonlyOption label="No" selected={!questionnaire.pharmacistNote || questionnaire.pharmacistNote === "no"} /><ReadonlyOption label={noteLabel} selected={Boolean(questionnaire.pharmacistNote && questionnaire.pharmacistNote !== "no")} detail={questionnaire.pharmacistNoteDetail ? valueOrDash(questionnaire.pharmacistNoteDetail) : ""} /></section><p className="questionnaire-disclaimer">The information provided is for pharmacist review and does not replace medical advice.</p></>}</div></div></div></div><div className="modal-backdrop fade show" onClick={onClose} /></>;
};

const OrderDetailModal = ({ show, item, view = "delivery", loadDetail = getOrder, onClose, onOrderUpdated, requestedStatusAction, hideOrderDetail = false, onWorkflowActionComplete }) => {
  const [order, setOrder] = useState(item);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState("");
  const [statusDialog, setStatusDialog] = useState(null);
  const [pharmacists, setPharmacists] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [assigneeId, setAssigneeId] = useState("");
  const [assigneeLoading, setAssigneeLoading] = useState(false);
  const openedActionRequestRef = useRef("");

  useEffect(() => {
    if (!show || !item?.id) return undefined;
    let active = true;
    setOrder(item);
    setError("");
    setStatusError("");
    setStatusSaving(false);
    setStatusDialog(null);
    setAssigneeId("");
    setLoading(true);
    loadDetail(item.id).then((detail) => { if (active) setOrder(detail); }).catch((loadError) => {
      if (active) setError(loadError.message || "Unable to load the complete order.");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [show, item, loadDetail]);

  const items = useMemo(() => getLineItems(order || {}), [order]);
  const prescription = items.filter((line) => line.itemType === "prescription");
  const otc = items.filter((line) => line.itemType === "otc");
  const patient = order?.patient || order?.beneficiary || order?.user || order?.customer || order?.client || order?.ownerUser || {};
  const phone = phoneNumberFor(order);
  const patientId = firstValue(order?.teoudatZehoutId, order?.teudatZehut, order?.identityNumber, patient.teoudatZehoutId, patient.teudatZehut, patient.identityNumber);
  const pharmacyFee = Number(firstValue(order?.pharmacyFee, order?.pharmacy_fee) ?? 0);
  const kupatHolimParticipation = Number(firstValue(
    order?.kupatHolimParticipation,
    order?.kupatHolimParticipationAmount,
    order?.participationAmount,
    order?.kupatHolimCoverage,
    order?.coverage,
  ) ?? 0);
  const orderTotal = Number(getOrderTotal(order) || 0);
  const lineItemsTotal = items.reduce((sum, line) => sum + itemTotal(line), 0);
  const total = lineItemsTotal > 0 ? lineItemsTotal : orderTotal;
  const explicitFinalTotal = firstValue(order?.finalTotal, order?.final_total, order?.finalAmount, order?.final_amount, order?.payableAmount, order?.netAmount, order?.amountPayable, order?.totalAfterParticipation);
  const finalTotal = explicitFinalTotal === undefined ? Math.max(0, total - kupatHolimParticipation) : Number(explicitFinalTotal);
  const status = String(order?.status || order?.orderStatus || "").toLowerCase();
  const nextStatus = status === "charged" ? "to_prepare" : status === "to_prepare" ? "prepared" : status === "prepared" ? "shipped" : null;
  const statusActionLabel = nextStatus === "to_prepare" ? "Move to To Prepare" : nextStatus === "prepared" ? "Mark as Prepared" : "Assign driver & ship";
  const questionnaire = questionnaireFor(order);
  const refreshOrder = async () => {
    const updatedOrder = await getOrder(order.id);
    setOrder(updatedOrder);
    await onOrderUpdated?.();
  };

  const updateStatus = async (statusValue, details) => {
    try {
      setStatusSaving(true);
      setStatusError("");
      await updateOrderStatus(order.id, statusValue, details);
      await refreshOrder();
      setStatusDialog(null);
      setAssigneeId("");
      if (requestedStatusAction) onWorkflowActionComplete?.();
    } catch (updateError) {
      setStatusError(updateError.message || "Unable to update order status.");
    } finally {
      setStatusSaving(false);
    }
  };

  const openStatusDialog = async () => {
    if (!nextStatus || statusSaving || !order?.id) return;

    if (nextStatus === "to_prepare") {
      await updateStatus(nextStatus);
      return;
    }

    if (nextStatus === "prepared") {
      const preparedById = getAuthenticatedUserId();
      if (!preparedById) {
        setStatusError("Unable to identify the logged-in pharmacist.");
        return;
      }
      await updateStatus(nextStatus, { preparedById });
      return;
    }

    try {
      setStatusError("");
      setAssigneeLoading(true);
      setAssigneeId("");
      if (nextStatus === "prepared") {
        const records = await listUsers({ role: "pharmacist", page: 1, limit: 100 });
        setPharmacists(records.filter((person) => person.isActive !== false));
        setStatusDialog("pharmacist");
      } else {
        const records = await listDeliveryDrivers();
        setDrivers(records.filter((driver) => driver.isActive !== false));
        setStatusDialog("driver");
      }
    } catch (loadError) {
      setStatusError(loadError.message || "Unable to load available staff.");
    } finally {
      setAssigneeLoading(false);
    }
  };

  const submitStatusDialog = async (event) => {
    event.preventDefault();
    if (!assigneeId) {
      setStatusError(`Select a ${statusDialog === "pharmacist" ? "pharmacist" : "driver"} first.`);
      return;
    }

    if (statusDialog === "pharmacist") {
      await updateStatus("prepared", { preparedById: Number(assigneeId) });
      return;
    }

    try {
      setStatusSaving(true);
      setStatusError("");
      await shipOrder(order.id, Number(assigneeId));
      await refreshOrder();
      setStatusDialog(null);
      setAssigneeId("");
      if (requestedStatusAction) onWorkflowActionComplete?.();
    } catch (updateError) {
      setStatusError(updateError.message || "Unable to ship this order.");
    } finally {
      setStatusSaving(false);
    }
  };

  useEffect(() => {
    const requestKey = requestedStatusAction?.requestId;
    if (
      !requestKey ||
      openedActionRequestRef.current === String(requestKey) ||
      loading ||
      statusSaving ||
      assigneeLoading ||
      requestedStatusAction.status !== nextStatus
    ) return;

    openedActionRequestRef.current = String(requestKey);
    openStatusDialog();
  }, [requestedStatusAction, loading, statusSaving, assigneeLoading, nextStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!show || !item) return null;

  if (view === "questionnaire") {
    return <HealthQuestionnaireModal order={order} item={item} loading={loading} error={error} onClose={onClose} />;
  }

  return <>
    {!hideOrderDetail && <>
    <div className="modal show delivery-note-modal" style={{ display: "block" }} tabIndex="-1" role="dialog" aria-modal="true" aria-label={`Delivery note for order ${order?.id || item.id}`} onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="modal-dialog modal-dialog-centered modal-xl delivery-note-dialog"><div className="modal-content">
        <div className="delivery-note-controls">
          <button type="button" onClick={() => window.print()}><i className="bi bi-printer"/> Print delivery note</button>
          {nextStatus && <button type="button" onClick={openStatusDialog} disabled={statusSaving || assigneeLoading}>{statusSaving ? "Updating…" : statusActionLabel}</button>}
          <button type="button" className="delivery-note-close" onClick={onClose} aria-label="Close"><i className="bi bi-x-lg"/></button>
        </div>
        <article className="delivery-note-printable">
          <header className="delivery-note-brand"><img src="/assets/img/kare-logo.png" alt="Kare"/><div><b>KARE PHARMACY</b><span>Tel Aviv-Yafo, Israel</span><span>contact@kare-pharm.com</span></div></header>
          <div className="delivery-note-rule"/>
          {loading ? <div className="delivery-note-loading">Loading order details…</div> : <>
            {error && <div className="delivery-note-error">{error} Showing the available order information.</div>}
            {statusError && <div className="delivery-note-error" role="alert">{statusError}</div>}
            <div className="delivery-note-title"><h1>{view === "questionnaire" ? "HEALTH QUESTIONNAIRE" : "BILLING"}</h1><span>{view === "questionnaire" ? "Patient health information" : "Patient billing summary"}</span></div>
            <section className="delivery-note-patient"><div className="delivery-note-patient-row"><Info label="Patient name">{getUserLabel(order)}</Info><Info label="ID number">{patientId}</Info><Info label="Phone number">{phone}</Info></div>{view !== "questionnaire" && <div className="delivery-note-patient-row delivery-note-address-row"><Info label="Delivery address">{getAddressLabel(order)}</Info><Info label="Delivery comments">{order?.deliveryComments}</Info></div>}</section>
            {view === "questionnaire" ? <section className="delivery-note-items"><div className="delivery-note-section-label">QUESTIONNAIRE RESPONSES</div><div className="delivery-note-patient-row"><Info label="Has allergies">{yesNo(questionnaire.hasAllergies)}</Info><Info label="Allergy details">{questionnaire.hasAllergies ? questionnaire.allergiesDetail : "Not applicable"}</Info></div><div className="delivery-note-patient-row"><Info label="Taking other medications">{yesNo(questionnaire.takingOtherMedications)}</Info><Info label="Other medication details">{questionnaire.takingOtherMedications ? questionnaire.otherMedicationsDetail : "Not applicable"}</Info></div><div className="delivery-note-patient-row"><Info label="Pharmacist note">{questionnaire.pharmacistNote}</Info><Info label="Pharmacist note details">{questionnaire.pharmacistNoteDetail}</Info></div></section> : <><section className="delivery-note-items"><div className="delivery-note-items-header"><span>Medication</span><span>Quantity</span><span>Unit price</span><span>Line total</span></div>
              {prescription.map((line, index) => <div className="delivery-note-item" key={line.id || `prescription-${index}`}><span>{getItemName(line)}</span><span>{line.quantity || 1}</span><span>{money(line.unitPrice ?? line.price)}</span><strong>{money(itemTotal(line))}</strong></div>)}
              {otc.length > 0 && <div className="delivery-note-section-label">OTC ITEMS SELECTED BY PATIENT</div>}
              {otc.map((line, index) => <div className="delivery-note-item" key={line.id || `otc-${index}`}><span>{getItemName(line)}</span><span>{line.quantity || 1}</span><span>{money(line.unitPrice ?? line.price)}</span><strong>{money(itemTotal(line))}</strong></div>)}
              {!items.length && <div className="delivery-note-no-items">No item details available.</div>}
            </section>
            <aside className="delivery-note-total"><div><span>Total</span><strong>{money(total)}</strong></div>{pharmacyFee > 0 && <div><span>Pharmacy fee</span><strong>{money(pharmacyFee)}</strong></div>}{kupatHolimParticipation > 0 && <div><span>Kupat Holim participation</span><strong>− {money(kupatHolimParticipation)}</strong></div>}<hr/><div className="delivery-note-final"><span>Final total</span><strong>{money(finalTotal)}</strong></div></aside></>}
          </>}
        </article>
      </div></div>
    </div>
    <div className="modal-backdrop fade show delivery-note-backdrop" onClick={onClose}/>
    </>}
    {statusDialog && <><div className="modal show" style={{ display: "block" }} tabIndex="-1" role="dialog" aria-modal="true"><div className="modal-dialog modal-dialog-centered"><form className="modal-content" onSubmit={submitStatusDialog}><div className="modal-header"><h5 className="modal-title">{statusDialog === "pharmacist" ? "Mark order as prepared" : "Assign driver and ship"}</h5><button type="button" className="btn-close" onClick={() => { if (!statusSaving) setStatusDialog(null); }} aria-label="Close" /></div><div className="modal-body"><label className="form-label" htmlFor="order-status-assignee">{statusDialog === "pharmacist" ? "Pharmacist who prepared this order" : "Delivery driver"}</label><select id="order-status-assignee" className="form-select" value={assigneeId} onChange={(event) => setAssigneeId(event.target.value)} disabled={assigneeLoading || statusSaving}><option value="">Select a {statusDialog === "pharmacist" ? "pharmacist" : "driver"}</option>{(statusDialog === "pharmacist" ? pharmacists : drivers).map((person) => <option key={person.id || person._id} value={person.id || person._id}>{person.name || person.fullName || person.email}</option>)}</select>{!assigneeLoading && !(statusDialog === "pharmacist" ? pharmacists : drivers).length && <p className="text-muted small mt-2 mb-0">No active {statusDialog === "pharmacist" ? "pharmacists" : "drivers"} are available.</p>}</div><div className="modal-footer"><button type="button" className="btn btn-light" onClick={() => setStatusDialog(null)} disabled={statusSaving}>Cancel</button><button type="submit" className="btn btn-primary" disabled={statusSaving || assigneeLoading || !(statusDialog === "pharmacist" ? pharmacists : drivers).length}>{statusSaving ? "Saving…" : statusDialog === "pharmacist" ? "Record pharmacist & prepare" : "Assign driver & ship"}</button></div></form></div></div><div className="modal-backdrop fade show" /></>}
  </>;
};

export default OrderDetailModal;
